import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext
} from 'react'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import ModulePicker from 'reports/form/module-picker'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Badge from 'react-bootstrap/Badge'
import Shield from 'assets/Shield/Gold/ThirdLineShieldGold.svg'
import {
  useCreateDashboard,
  useUpdateDashboard
} from 'api/hooks/use-dashboards'
import {
  useDashboardTemplates,
  useApplyDashboardTemplate
} from 'api/hooks/use-dashboard-templates'
import { useTableColumns } from 'api/hooks/use-table-columns'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from 'react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen } from '@fortawesome/free-solid-svg-icons'
import FilterEditor from './template-creation/filter-editor'
import AccountGroupingSelect from './account-grouping-select'
import { useActiveAccountGrouping } from 'api/hooks/use-account-groupings'
import VendorSearch from './filters/vendors'
import EmployeeSearch from './filters/employees'
import Analytics from './filters/analytics'
import AccountCodeFilter from './filters/account-code'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'
import { apiPost } from 'api'
import { viewPath } from 'helpers/utils'
import { getColumnDisplayName } from './utils'
import { makeGetColumnOptions } from 'shared/column-filters/getColumnOptions'

// Map account types from snake_case (backend) to camelCase (frontend params)
const ACCOUNT_TYPE_MAP = {
  org: 'orgs',
  object: 'objects',
  project: 'projects',
  seg_one: 'segOnes',
  seg_two: 'segTwos',
  seg_three: 'segThrees',
  seg_four: 'segFours',
  seg_five: 'segFives',
  seg_six: 'segSixes',
  seg_seven: 'segSevens',
  seg_eight: 'segEights'
}

function NewView({
  sectionId = '',
  sectionName = '',
  setDashboardId,
  dashboard,
  setParams,
  baseParams,
  isUserCreator = true,
  initialAnalytics = null,
  size = 'md',
  initialParams = null,
  // Controlled mode: pass `show`/`onHide` and NewView renders no trigger of its own, so the Create
  // chooser can open it after the user picks "View". Omit both and it behaves as it always has —
  // own button, own state — which is what dashboard/show.jsx relies on.
  show: showProp,
  onHide,
  // The folder the index was showing when Create was pressed. Both creation paths below honor it —
  // from scratch and from a template — or creating from a template inside a folder would quietly
  // drop the View at the top level.
  folderId = null
}) {
  const isControlled = showProp !== undefined
  const isEdit = !!dashboard
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const token = useContext(AuthContext)
  const { municipality } = useContext(UserContext)
  const [showState, setShowState] = useState(false)
  const show = isControlled ? showProp : showState
  const setShow = isControlled
    ? (next) => { if (!next) onHide?.() }
    : setShowState
  const [currentStep, setCurrentStep] = useState(1)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [creationMode, setCreationMode] = useState('template') // 'template' or 'custom'
  const [templateFilterValues, setTemplateFilterValues] = useState({})
  const [initialTemplateFilterValues, setInitialTemplateFilterValues] =
    useState({})

  const handleClose = () => {
    setShow(false)
    setCurrentStep(1)
    setSelectedTemplate(null)
    setCreationMode('template')
    setTemplateFilterValues({})
    setInitialTemplateFilterValues({})
  }

  const prepareForm = () => {
    if (isEdit) {
      setForm({
        section_id: dashboard.section_id,
        name: dashboard.name,
        description: dashboard.description || '',
        // Left blank when the View never chose, so a rename doesn't quietly pin the View to
        // whatever the default is today.
        account_grouping_id: dashboard.account_grouping_id || ''
      })
    } else {
      setForm(defaultParams)
      // If initialAnalytics is provided, skip template selection and go to custom view
      if (initialAnalytics && initialAnalytics.length > 0) {
        setCreationMode('custom')
        setCurrentStep(2)
      } else if (sectionId) {
        // If section is pre-selected, skip to step 2
        setCurrentStep(2)
      } else {
        setCurrentStep(1)
      }
    }
  }

  const handleShow = () => {
    prepareForm()
    setShow(true)
  }

  const defaultParams = {
    section_id: sectionId,
    name: initialParams?.name || '',
    description: initialParams?.description || ''
  }
  const [form, setForm] = useState(defaultParams)

  // Controlled mode has no trigger button to run handleShow, so do its setup when the caller opens
  // the modal. Runs only on the closed → open edge.
  useEffect(() => {
    if (isControlled && showProp) prepareForm()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isControlled, showProp])

  // Auto-advance from step 1 to step 2 when module is selected
  useEffect(() => {
    if (currentStep === 1 && form.section_id && !sectionId) {
      setCurrentStep(2)
    }
  }, [form.section_id, currentStep, sectionId])

  // Fetch templates when section is selected
  const { data: templates, isLoading: templatesLoading } =
    useDashboardTemplates({
      params: { section_id: form.section_id }
    })

  // Fetch table columns for the selected template's section
  const { data: tableColumns = [] } = useTableColumns({
    params: { section_id: selectedTemplate?.section_id },
    options: { enabled: !!selectedTemplate?.section_id }
  })

  // Resolve filter options from the table_columns API response.
  const getColumnOptions = makeGetColumnOptions(tableColumns, municipality)

  useEffect(() => {
    if (selectedTemplate) {
      // Extract account search types and operators from template
      const accountSearchTypes =
        selectedTemplate.account_search?.account_search_codes
          ?.map((code) => code.account_type)
          .filter(Boolean)
          .filter((v, i, a) => a.indexOf(v) === i) || []

      const accountSearchOperators = {}
      if (selectedTemplate.account_search?.operators) {
        Object.entries(selectedTemplate.account_search.operators).forEach(
          ([key, value]) => {
            // Convert snake_case to camelCase (orgs_operator -> orgsOperator)
            const camelKey = key.replace(/_operator$/, 'Operator')
            accountSearchOperators[camelKey] = value
          }
        )
      }

      const templateConfig = {
        vendors:
          selectedTemplate.vendors
            ?.filter((v) => v.options !== 'exclude')
            .map((v) => ({
              id: v.vendor_id || v.id,
              name: v.vendor?.name,
              ...v,
              options: v.options || 'include'
            })) || [],
        excludedVendors:
          selectedTemplate.vendors
            ?.filter((v) => v.options === 'exclude')
            .map((v) => ({
              id: v.vendor_id || v.id,
              name: v.vendor?.name,
              ...v,
              options: 'exclude'
            })) || [],
        employees:
          selectedTemplate.employees
            ?.filter((e) => e.options !== 'exclude')
            .map((e) => ({
              id: e.employee_id || e.id,
              name: e.employee?.name,
              external_id: e.employee?.external_id,
              display_name: e.employee?.display_name,
              full_name: e.employee?.full_name,
              database_column_name: e.database_column_name,
              options: e.options || 'include'
            })) || [],
        excludedEmployees:
          selectedTemplate.employees
            ?.filter((e) => e.options === 'exclude')
            .map((e) => ({
              id: e.employee_id || e.id,
              name: e.employee?.name,
              external_id: e.employee?.external_id,
              display_name: e.employee?.display_name,
              full_name: e.employee?.full_name,
              database_column_name: e.database_column_name,
              options: 'exclude'
            })) || [],
        analytics:
          selectedTemplate.analytics
            ?.filter((a) => a.options !== 'exclude')
            .map((a) => ({
              id: a.analytic_id || a.id,
              name: a.analytic?.name,
              description: a.analytic?.description,
              ...a,
              options: a.options || 'include'
            })) || [],
        excludedAnalytics:
          selectedTemplate.analytics
            ?.filter((a) => a.options === 'exclude')
            .map((a) => ({
              id: a.analytic_id || a.id,
              name: a.analytic?.name,
              description: a.analytic?.description,
              ...a,
              options: 'exclude'
            })) || [],
        section_id: selectedTemplate.section_id,
        minimum_amount: selectedTemplate.minimum_amount,
        maximum_amount: selectedTemplate.maximum_amount,
        accountSearchTypes,
        accountSearchOperators,
        ...(selectedTemplate.params || {})
      }

      // Clear reference filters (vendor, employee) so users select accessible ones
      // Analytics keep template values as they have no access restrictions
      const initialValues = {
        ...templateConfig,
        vendors: [],
        excludedVendors: [],
        employees: [],
        excludedEmployees: []
      }

      setTemplateFilterValues(initialValues)
      setInitialTemplateFilterValues(templateConfig)
    }
  }, [selectedTemplate])

  const handleTemplateFilterChange = useCallback((newParams) => {
    setTemplateFilterValues(newParams)
  }, [])

  const requiredFilterKeys = useMemo(() => {
    if (!selectedTemplate?.required_filter_prompts?.prompts) return []
    return selectedTemplate.required_filter_prompts.prompts
      .filter((p) => p.is_required)
      .map((p) => p.accessor_key)
  }, [selectedTemplate])

  const columnHasValue = useCallback(
    (column) => {
      if (['min_max', 'date', 'min_max_percent'].includes(column.filter_type)) {
        const prefix = column.section_abbreviation?.toLowerCase() || ''
        const suffix = column.accessor_key.slice(prefix.length)
        const minKey = `${prefix}Min${suffix}`
        const maxKey = `${prefix}Max${suffix}`
        const hasMinMax =
          initialTemplateFilterValues[minKey] ||
          initialTemplateFilterValues[maxKey]

        if (column.filter_type === 'date') {
          const optionKey = `${column.accessor_key}Option`
          const hasOption =
            initialTemplateFilterValues[optionKey] &&
            initialTemplateFilterValues[optionKey] !== 'any'
          return hasMinMax || hasOption
        }
        return hasMinMax
      }

      const value = initialTemplateFilterValues[column.accessor_key]
      return (
        value && value !== '' && !(Array.isArray(value) && value.length === 0)
      )
    },
    [initialTemplateFilterValues]
  )

  const isFilterRequired = useCallback(
    (column) => requiredFilterKeys.includes(column.accessor_key),
    [requiredFilterKeys]
  )

  const hasRequiredFilterValue = useCallback(
    (column) => {
      const key = column.accessor_key

      if (['min_max', 'date', 'min_max_percent'].includes(column.filter_type)) {
        const prefix = column.section_abbreviation?.toLowerCase() || ''
        const suffix = column.accessor_key.slice(prefix.length)
        const minKey = `${prefix}Min${suffix}`
        const maxKey = `${prefix}Max${suffix}`
        const hasMinMax =
          templateFilterValues[minKey] || templateFilterValues[maxKey]

        if (column.filter_type === 'date') {
          const optionKey = `${column.accessor_key}Option`
          const hasOption =
            templateFilterValues[optionKey] &&
            templateFilterValues[optionKey] !== 'any'
          return hasMinMax || hasOption
        }

        return hasMinMax
      }

      const value = templateFilterValues[key]
      return (
        value && value !== '' && !(Array.isArray(value) && value.length === 0)
      )
    },
    [templateFilterValues]
  )

  const allRequiredFiltersHaveValues = useMemo(() => {
    const requiredColumns = tableColumns.filter((col) => isFilterRequired(col))
    const tableFiltersValid =
      requiredColumns.length === 0 ||
      requiredColumns.every((col) => hasRequiredFilterValue(col))

    const templateHasVendors =
      (initialTemplateFilterValues.vendors &&
        initialTemplateFilterValues.vendors.length > 0) ||
      (initialTemplateFilterValues.excludedVendors &&
        initialTemplateFilterValues.excludedVendors.length > 0)
    const currentHasVendors =
      (templateFilterValues.vendors &&
        templateFilterValues.vendors.length > 0) ||
      (templateFilterValues.excludedVendors &&
        templateFilterValues.excludedVendors.length > 0)
    const vendorsValid = !templateHasVendors || currentHasVendors

    const templateHasEmployees =
      (initialTemplateFilterValues.employees &&
        initialTemplateFilterValues.employees.length > 0) ||
      (initialTemplateFilterValues.excludedEmployees &&
        initialTemplateFilterValues.excludedEmployees.length > 0)
    const currentHasEmployees =
      (templateFilterValues.employees &&
        templateFilterValues.employees.length > 0) ||
      (templateFilterValues.excludedEmployees &&
        templateFilterValues.excludedEmployees.length > 0)
    const employeesValid = !templateHasEmployees || currentHasEmployees

    const templateHasAccounts =
      initialTemplateFilterValues.accountSearchTypes &&
      initialTemplateFilterValues.accountSearchTypes.length > 0
    const currentHasAccounts =
      templateHasAccounts &&
      initialTemplateFilterValues.accountSearchTypes.some((accountType) => {
        const paramKey = ACCOUNT_TYPE_MAP[accountType]
        return (
          templateFilterValues[paramKey] &&
          templateFilterValues[paramKey].length > 0
        )
      })
    const accountsValid = !templateHasAccounts || currentHasAccounts

    return tableFiltersValid && vendorsValid && employeesValid && accountsValid
  }, [
    tableColumns,
    isFilterRequired,
    hasRequiredFilterValue,
    templateFilterValues,
    initialTemplateFilterValues
  ])

  const { mutate: applyTemplate } = useApplyDashboardTemplate({
    options: {
      onSuccess: (dashboard) => {
        handleClose()
        queryClient.invalidateQueries('dashboards')
        navigate(viewPath(dashboard.section_name, dashboard.id) || '/dashboards')
      },
      onError: (error) => {
        console.error('Failed to apply template:', error)
      }
    }
  })

  const createDashboardRelationships = async (dashboardId, params) => {
    const promises = []

    const allVendors = [
      ...(params.vendors || []).map((v) => ({ ...v, options: 'include' })),
      ...(params.excludedVendors || []).map((v) => ({
        ...v,
        options: 'exclude'
      }))
    ]

    allVendors.forEach((vendor) => {
      const vendorPromise = apiPost({
        url: `dashboards/${dashboardId}/dashboard_vendors`,
        token,
        objectName: 'dashboard_vendor',
        params: {
          vendor_id: vendor.id,
          dashboard_id: dashboardId,
          options: vendor.options
        }
      })
      promises.push(vendorPromise)
    })

    const allEmployees = [
      ...(params.employees || []).map((e) => ({ ...e, options: 'include' })),
      ...(params.excludedEmployees || []).map((e) => ({
        ...e,
        options: 'exclude'
      }))
    ]

    allEmployees.forEach((employee) => {
      const employeePromise = apiPost({
        url: `dashboards/${dashboardId}/dashboard_employees`,
        token,
        objectName: 'dashboard_employee',
        params: {
          employee_id: employee.id,
          dashboard_id: dashboardId,
          options: employee.options,
          database_column_name: employee.database_column_name
        }
      })
      promises.push(employeePromise)
    })

    const analytics = params.analytics || []
    analytics.forEach((analytic) => {
      const analyticPromise = apiPost({
        url: `dashboards/${dashboardId}/dashboard_analytics`,
        token,
        objectName: 'dashboard_analytic',
        params: {
          analytic_id: analytic.id,
          dashboard_id: dashboardId,
          options: analytic.options || 'include'
        }
      })
      promises.push(analyticPromise)
    })

    if (promises.length > 0) {
      await Promise.all(promises)
    }
  }

  const options = {
    onSuccess: async (data) => {
      // If creating a new dashboard (not editing), create the queued vendors/employees/analytics
      if (!isEdit && (baseParams || initialAnalytics)) {
        try {
          // Merge baseParams with initialAnalytics if both exist
          const relationshipParams = baseParams
            ? { ...baseParams }
            : { analytics: [] }

          // Add initialAnalytics to the analytics array (for filtering) and analyticsColumns (for table display)
          if (initialAnalytics) {
            relationshipParams.analytics = [
              ...(relationshipParams.analytics || []),
              ...initialAnalytics
            ]
            relationshipParams.analyticsColumns = [
              ...(relationshipParams.analyticsColumns || []),
              ...initialAnalytics
            ]
          }

          await createDashboardRelationships(data.id, relationshipParams)
        } catch (error) {
          console.error('Failed to create dashboard relationships:', error)
        }
      }

      handleClose()
      queryClient.invalidateQueries('dashboards')
      queryClient.invalidateQueries('current-user')
      if (isEdit) {
        queryClient.invalidateQueries([`dashboards/${dashboard.id}`])
        // A new grouping re-buckets every rollup column, so the charts and grid are stale in
        // a way a rename never makes them.
        if (groupingChanged) {
          queryClient.invalidateQueries('dashboard-chart-data')
          queryClient.invalidateQueries('transactions')
        }
        setParams && setParams((p) => ({ ...p, name: form.name }))
      } else {
        baseParams && setParams(baseParams)
        setDashboardId && setDashboardId(data.id)
        navigate(viewPath(data.section_name, data.id) || '/dashboards')
      }
    },
    onError: (error) => {
      console.error('Failed to create/update dashboard:', error)
    }
  }

  const createParams = {
    section_id: form.section_id || sectionId,
    name: form.name,
    description: form.description,
    folder_id: folderId
  }

  // '' means "no choice of its own", so send null rather than a blank uuid.
  const updateParams = {
    name: form.name,
    description: form.description,
    account_grouping_id: form.account_grouping_id || null
  }
  // A View being created has no grouping of its own yet, so the template's column
  // list names the district default — which is what that View will report under.
  const groupingName =
    useActiveAccountGrouping(dashboard?.id)?.name || null
  const groupingChanged =
    isEdit &&
    (form.account_grouping_id || null) !== (dashboard?.account_grouping_id || null)
  const { mutate: createDashboard } = useCreateDashboard({
    params: createParams,
    options
  })
  const { mutate: updateDashboard } = useUpdateDashboard({
    id: dashboard?.id,
    options
  })

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template)
    setForm({
      ...form,
      name: template.name,
      description: template.description || ''
    })
    setCurrentStep(3)
  }

  const handleApplyTemplate = () => {
    if (!selectedTemplate) return

    applyTemplate({
      templateId: selectedTemplate.id,
      folderId,
      userFilterValues: {
        ...templateFilterValues,
        name: form.name,
        description: form.description
      }
    })
  }

  return (
    <React.Fragment>
      {isControlled ? null : isEdit && isUserCreator ? (
        <Button className="btn btn-sm btn-light" onClick={handleShow} size="sm">
          <FontAwesomeIcon icon={faPen} /> Edit
        </Button>
      ) : !isEdit ? (
        <Button variant="primary" size={size} onClick={handleShow}>
          <img src={Shield} style={{ width: 15 }} alt="ThirdLine Shield" />{' '}
          Create View
        </Button>
      ) : null}
      <Modal show={show} onHide={handleClose} size="lg" enforceFocus={false}>
        <Modal.Header closeButton>
          <Modal.Title>{isEdit ? 'Edit View' : 'Add View'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isEdit ? (
            <>
              <Row className="mt-2">
                <Col>
                  <Form.Group>
                    <Form.Label>Name of View</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="View Name"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <Form.Group>
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Enter a description for this view"
                      value={form.description}
                      onChange={(e) =>
                        setForm({ ...form, description: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col>
                  <AccountGroupingSelect
                    value={form.account_grouping_id}
                    onChange={(id) =>
                      setForm({ ...form, account_grouping_id: id })
                    }
                  />
                </Col>
              </Row>
            </>
          ) : (
            <>
              {currentStep === 1 && !sectionId && (
                <>
                  <div className="h5 mt-2 mb-3">Select Dataset</div>
                  <ModulePicker
                    setParams={setForm}
                    params={form}
                    limitModules={[
                      'AP',
                      'GL',
                      'PO',
                      'RQ',
                      'PC',
                      'Vendor',
                      'VO',
                      'VR',
                      'PR',
                      'BG',
                      'BOT',
                      'WF',
                      'PWF',
                      'APLI',
                      'OKGT',
                      'CT',
                      'CH',
                      'UBAccounts',
                      'UBAdjustments',
                      'UBBilling',
                      'UBConsumption',
                      'UBFixedUsage'
                    ]}
                  />
                </>
              )}

              {currentStep === 2 && (
                <>
                  <div className="h5 mt-2 mb-3">Choose Creation Method</div>

                  <div className="mb-4">
                    <div className="btn-group w-100" role="group">
                      <Button
                        variant={
                          creationMode === 'template' ? 'primary' : 'light'
                        }
                        onClick={() => setCreationMode('template')}
                      >
                        Use Template
                      </Button>
                      <Button
                        variant={
                          creationMode === 'custom' ? 'primary' : 'light'
                        }
                        onClick={() => {
                          setCreationMode('custom')
                          setSelectedTemplate(null)
                          setForm({ ...form, name: '', description: '' })
                        }}
                      >
                        Start from Scratch
                      </Button>
                    </div>
                  </div>

                  {creationMode === 'template' && (
                    <>
                      <div className="h6 mb-3">Select Template</div>
                      {templatesLoading ? (
                        <p className="text-muted">Loading templates...</p>
                      ) : templates && templates.length > 0 ? (
                        <Row
                          className="g-3"
                          style={{ maxHeight: '400px', overflowY: 'auto' }}
                        >
                          {templates.map((template) => (
                            <Col key={template.id} xs={12} md={6}>
                              <Card
                                className={`cursor-pointer h-100 ${
                                  selectedTemplate?.id === template.id
                                    ? 'border-primary'
                                    : ''
                                }`}
                                onClick={() => handleTemplateSelect(template)}
                                style={{ cursor: 'pointer' }}
                              >
                                <Card.Body className="p-3">
                                  <h6 className="mb-1">{template.name}</h6>
                                  <p className="text-muted small mb-2">
                                    {template.description}
                                  </p>
                                  <div className="d-flex gap-2 small text-muted">
                                    <span>
                                      📊 {template.charts?.length || 0}
                                    </span>
                                    <span>
                                      📋{' '}
                                      {(template.columns?.length || 0) +
                                        (template.custom_columns?.length || 0)}
                                    </span>
                                    {template.has_required_prompts && (
                                      <Badge bg="warning" text="dark">
                                        Requires input
                                      </Badge>
                                    )}
                                  </div>
                                </Card.Body>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      ) : (
                        <p className="text-muted small">
                          No templates available for this module
                        </p>
                      )}
                    </>
                  )}

                  {creationMode === 'custom' && (
                    <>
                      <div className="alert alert-info">
                        <strong>Start from Scratch:</strong> You'll create a blank
                        view that you can customize with your own filters and
                        settings.
                      </div>
                      <Row className="mb-3">
                        <Col>
                          <Form.Group controlId="viewName">
                            <Form.Label>Name of View</Form.Label>
                            <Form.Control
                              type="text"
                              placeholder="View Name"
                              value={form.name}
                              onChange={(e) =>
                                setForm({ ...form, name: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col>
                          <Form.Group controlId="viewDescription">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Enter a description for this view"
                              value={form.description}
                              onChange={(e) =>
                                setForm({ ...form, description: e.target.value })
                              }
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              {currentStep === 3 && (
                <>
                  <div className="h5 mt-2 mb-3">
                    {creationMode === 'template' && selectedTemplate
                      ? `Customize "${selectedTemplate.name}"`
                      : 'Create Your View'}
                  </div>

                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="viewName">
                        <Form.Label>Name of View</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="View Name"
                          value={form.name}
                          onChange={(e) =>
                            setForm({ ...form, name: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col>
                      <Form.Group controlId="viewDescription">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="Enter a description for this view"
                          value={form.description}
                          onChange={(e) =>
                            setForm({ ...form, description: e.target.value })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {creationMode === 'template' &&
                    selectedTemplate &&
                    tableColumns.length > 0 && (
                      <div className="mt-3">
                        <h6 className="mb-3">Pre-configured Filters</h6>
                        <div>
                          {tableColumns
                            .filter((col) => {
                              // Skip analytics columns - they are handled separately below
                              // with the proper Analytics component that has Off/Include/Exclude toggles
                              const hasTemplateAnalytics =
                                (initialTemplateFilterValues.analytics &&
                                  initialTemplateFilterValues.analytics.length > 0) ||
                                (initialTemplateFilterValues.excludedAnalytics &&
                                  initialTemplateFilterValues.excludedAnalytics.length > 0)
                              if (
                                hasTemplateAnalytics &&
                                (col.accessor_key?.toLowerCase().includes('analytics') ||
                                  col.name?.toLowerCase() === 'analytics')
                              ) {
                                return false
                              }

                              if (
                                requiredFilterKeys.includes(col.accessor_key)
                              ) {
                                return true
                              }
                              return columnHasValue(col)
                            })
                            .map((col) => (
                              <FilterEditor
                                key={col.id}
                                column={col}
                                params={templateFilterValues}
                                setParams={handleTemplateFilterChange}
                                getOptions={getColumnOptions}
                                label={getColumnDisplayName(col, municipality, groupingName)}
                                isUserCreator={true}
                                requiresChange={isFilterRequired(col)}
                                hasBeenModified={hasRequiredFilterValue(col)}
                                hideApplyButton={true}
                                initialTemplateFilterValues={
                                  initialTemplateFilterValues
                                }
                              />
                            ))}

                          {((initialTemplateFilterValues.vendors &&
                            initialTemplateFilterValues.vendors.length > 0) ||
                            (initialTemplateFilterValues.excludedVendors &&
                              initialTemplateFilterValues.excludedVendors
                                .length > 0)) && (
                            <VendorSearch
                              params={templateFilterValues}
                              setParams={handleTemplateFilterChange}
                              showAsAccordion={true}
                              isRequired={
                                !(
                                  (templateFilterValues.vendors &&
                                    templateFilterValues.vendors.length > 0) ||
                                  (templateFilterValues.excludedVendors &&
                                    templateFilterValues.excludedVendors
                                      .length > 0)
                                )
                              }
                              label="Vendor(s)"
                            />
                          )}

                          {initialTemplateFilterValues.accountSearchTypes &&
                            initialTemplateFilterValues.accountSearchTypes
                              .length > 0 &&
                            initialTemplateFilterValues.accountSearchTypes.map(
                              (accountType) => {
                                const paramKey = ACCOUNT_TYPE_MAP[accountType]
                                const hasValue =
                                  templateFilterValues[paramKey] &&
                                  templateFilterValues[paramKey].length > 0
                                const operatorKey = `${paramKey}Operator`
                                const initialOperator =
                                  initialTemplateFilterValues
                                    .accountSearchOperators?.[operatorKey] ||
                                  'include'

                                return (
                                  <AccountCodeFilter
                                    key={accountType}
                                    accountType={accountType}
                                    params={templateFilterValues}
                                    setParams={handleTemplateFilterChange}
                                    showAsAccordion={true}
                                    isRequired={!hasValue}
                                    initialOperator={initialOperator}
                                  />
                                )
                              }
                            )}

                          {(() => {
                            const allTemplateEmployees = [
                              ...(initialTemplateFilterValues.employees || []),
                              ...(initialTemplateFilterValues.excludedEmployees ||
                                [])
                            ]

                            if (allTemplateEmployees.length === 0) return null

                            const employeesByColumn =
                              allTemplateEmployees.reduce((acc, emp) => {
                                const colName = emp.database_column_name
                                if (!acc[colName]) {
                                  acc[colName] = []
                                }
                                acc[colName].push(emp)
                                return acc
                              }, {})

                            return Object.entries(employeesByColumn).map(
                              ([columnName, _employees]) => {
                                const tableColumn = tableColumns.find(
                                  (col) =>
                                    col.database_column_name === columnName
                                )
                                const label = tableColumn?.name || 'Employee(s)'

                                const currentEmployees =
                                  templateFilterValues.employees || []
                                const currentExcludedEmployees =
                                  templateFilterValues.excludedEmployees || []
                                const hasCurrentValues =
                                  currentEmployees.some(
                                    (e) => e.database_column_name === columnName
                                  ) ||
                                  currentExcludedEmployees.some(
                                    (e) => e.database_column_name === columnName
                                  )

                                return (
                                  <EmployeeSearch
                                    key={columnName}
                                    params={templateFilterValues}
                                    setParams={handleTemplateFilterChange}
                                    columnName={columnName}
                                    showAsAccordion={true}
                                    isRequired={!hasCurrentValues}
                                    label={label}
                                  />
                                )
                              }
                            )
                          })()}

                          {(() => {
                            const allTemplateAnalytics = [
                              ...(initialTemplateFilterValues.analytics || []),
                              ...(initialTemplateFilterValues.excludedAnalytics ||
                                [])
                            ]

                            const uniqueAnalytics = Array.from(
                              new Map(
                                allTemplateAnalytics.map((a) => [
                                  a.id,
                                  {
                                    id: a.id,
                                    name: a.name,
                                    description: a.description
                                  }
                                ])
                              ).values()
                            )

                            if (uniqueAnalytics.length === 0) return null

                            return (
                              <Analytics
                                params={templateFilterValues}
                                setParams={handleTemplateFilterChange}
                                availableAnalytics={uniqueAnalytics}
                                showAsAccordion={true}
                                isRequired={false}
                                label="Analytics"
                              />
                            )
                          })()}
                        </div>

                        {!allRequiredFiltersHaveValues && (
                          <div className="alert alert-warning mt-2 mb-0 small">
                            <strong>Required:</strong> You must provide values
                            for all filters marked with an asterisk (*) before
                            creating the dashboard.
                          </div>
                        )}
                      </div>
                    )}
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          {isEdit ? (
            <>
              <Button variant="white" onClick={handleClose}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => updateDashboard(updateParams)}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <div className="d-flex justify-content-between w-100">
                <div>
                  {currentStep > (sectionId ? 2 : 1) && (
                    <Button
                      variant="light"
                      onClick={() => {
                        const prevStep = currentStep - 1
                        if (prevStep === 1 && !sectionId) {
                          setForm((f) => ({ ...f, section_id: null }))
                          setSelectedTemplate(null)
                          setCreationMode('template')
                        }
                        if (prevStep === 2) {
                          setSelectedTemplate(null)
                        }
                        setCurrentStep(prevStep)
                      }}
                    >
                      Back
                    </Button>
                  )}
                </div>
                <div>
                  <Button
                    variant="white"
                    onClick={handleClose}
                    className="me-2"
                  >
                    Cancel
                  </Button>

                  {currentStep === 2 && creationMode === 'custom' && (
                    <Button
                      variant="primary"
                      onClick={() => createDashboard()}
                      disabled={!form.name}
                    >
                      Create View
                    </Button>
                  )}

                  {currentStep === 3 && (
                    <>
                      {creationMode === 'custom' ? (
                        <Button
                          variant="primary"
                          onClick={() => createDashboard()}
                          disabled={!form.name}
                        >
                          Create View
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          onClick={handleApplyTemplate}
                          disabled={
                            !selectedTemplate ||
                            !form.name ||
                            !allRequiredFiltersHaveValues
                          }
                          title={
                            !selectedTemplate
                              ? 'Please select a template'
                              : !form.name
                              ? 'Please enter a name for the dashboard'
                              : !allRequiredFiltersHaveValues
                              ? 'Please provide values for all required filters before creating the dashboard'
                              : 'Create dashboard from template'
                          }
                        >
                          Create from Template
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}

export default NewView
